import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import BrandsTableDetails from "./BrandsTableDetails";
import { PlusIcon } from "@heroicons/react/24/outline";
import AddBrandForm from "./AddBrandForm";
import {
  deleteBrandById,
  fetchAllBrand,
  updateBrandByIdForSortNumber,
} from "../../../../redux/reducer/brandReducer";
import { fetchAllCategory } from "../../../../redux/reducer/categoryReducer";

const BrandsSettings = () => {
  const [editingOrder, setEditingOrder] = useState(null);
  const [pricingOrder, setPricingOrder] = useState(null);
  const dispatch = useDispatch();
  const { allBrands, loading, error } = useSelector((state) => state.brands);
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);

  const [selectedArea, setSelectedArea] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");

  const [unfilteredBrands, setUnfilteredBrands] = useState([]);

  const [categorySubCategories, setCategorySubCategories] = useState([]);

  const [allCategories, setAllCategories] = useState([]);

  const headers = [
    "ID",
    "Title EN",
    "Title AR",
    "Link",
    "image",
    "Area",
    "Category",
    "SubCategory",
    "Actions",
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const brandsResult = await dispatch(fetchAllBrand()).unwrap();
        setUnfilteredBrands(brandsResult);

        const categoriesResult = await dispatch(fetchAllCategory()).unwrap();
        setAllCategories(categoriesResult);
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      }
    };

    fetchInitialData();
  }, [dispatch]);

  useEffect(() => {
    if (selectedArea) {
      setSelectedCategory("");
      setSelectedSubCategory("");
    }
  }, [selectedArea]);

  useEffect(() => {
    if (selectedCategory) {
      setSelectedSubCategory("");
      fetchCategorySubCategories(selectedCategory);
    } else {
      setCategorySubCategories([]);
    }
  }, [selectedCategory]);

  const fetchCategorySubCategories = async (categoryId) => {
    try {
      const selectedCategoryData = allCategories.find(
        (cat) => cat._id === categoryId
      );
      if (selectedCategoryData && selectedCategoryData.subCategory) {
        setCategorySubCategories(selectedCategoryData.subCategory);
      } else {
        setCategorySubCategories([]);
      }
    } catch (error) {
      console.error("Failed to get category subcategories:", error);
      setCategorySubCategories([]);
    }
  };

  // Fetch filtered data when filters change
  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        if (!selectedArea && !selectedCategory && !selectedSubCategory) {
          await dispatch(fetchAllBrand()).unwrap();
          return;
        }

        const params = new URLSearchParams();
        if (selectedArea) params.append("area", selectedArea);
        if (selectedCategory) params.append("category", selectedCategory);
        if (selectedSubCategory)
          params.append("subCategory", selectedSubCategory);

        await dispatch(fetchAllBrand(params.toString())).unwrap();
      } catch (error) {
        console.error("Failed to fetch filtered brands:", error);
      }
    };

    fetchFilteredData();
  }, [selectedArea, selectedCategory, selectedSubCategory, dispatch]);

  const loadBrands = async () => {
    try {
      await dispatch(fetchAllBrand()).unwrap();
    } catch (error) {
      console.error("Failed to fetch the brands:", error);
    }
  };

  const handleDelete = (brandId) => {
    if (window.confirm("Are you sure you want to delete this Brand?")) {
      dispatch(deleteBrandById(brandId))
        .unwrap()
        .then(() => {
          alert(t("BrandDeleted"));
          const params = new URLSearchParams();
          if (selectedArea) params.append("area", selectedArea);
          if (selectedCategory) params.append("category", selectedCategory);
          if (selectedSubCategory)
            params.append("subCategory", selectedSubCategory);
          dispatch(fetchAllBrand(params.toString() || undefined));
        })
        .catch((error) => alert(error || "Failed to delete Brand."));
    }
  };
  const handleOnDown = (
    firstBrandId,
    secondBrandId,
    firstSortNum,
    secondSortNum
  ) => {
    let id = firstBrandId;
    let data = { sortNumber: firstSortNum + 1 };
    dispatch(updateBrandByIdForSortNumber({ id, data }));
    id = secondBrandId;
    data = { sortNumber: secondSortNum - 1 };
    dispatch(updateBrandByIdForSortNumber({ id, data }))
      .unwrap()
      .then(() => {
        const params = new URLSearchParams();
        if (selectedArea) params.append("area", selectedArea);
        if (selectedCategory) params.append("category", selectedCategory);
        if (selectedSubCategory)
          params.append("subCategory", selectedSubCategory);
        dispatch(fetchAllBrand(params.toString() || undefined));
      })
      .catch((error) => alert(error || "Failed to order Brand."));
  };

  const handleOnTop = (
    firstBrandId,
    secondBrandId,
    firstSortNum,
    secondSortNum
  ) => {
    let id = firstBrandId;
    let data = { sortNumber: firstSortNum - 1 };
    dispatch(updateBrandByIdForSortNumber({ id, data }));
    id = secondBrandId;
    data = { sortNumber: secondSortNum + 1 };
    dispatch(updateBrandByIdForSortNumber({ id, data }))
      .unwrap()
      .then(() => {
        const params = new URLSearchParams();
        if (selectedArea) params.append("area", selectedArea);
        if (selectedCategory) params.append("category", selectedCategory);
        if (selectedSubCategory)
          params.append("subCategory", selectedSubCategory);
        dispatch(fetchAllBrand(params.toString() || undefined));
      })
      .catch((error) => alert(error || "Failed to order Brand."));
  };

  const handleUpdate = () => {
    const params = new URLSearchParams();
    if (selectedArea) params.append("area", selectedArea);
    if (selectedCategory) params.append("category", selectedCategory);
    if (selectedSubCategory) params.append("subCategory", selectedSubCategory);
    dispatch(fetchAllBrand(params.toString() || undefined));
  };

  return (
    <div className="mx-auto overflow-x-auto rounded-xl">
      <div className="flex justify-between my-4">
        <h1 className="text-xl font-bold text-black">Brands</h1>

        {/* Filter */}
        <div>
          <div className="flex flex-wrap gap-3 ">
            {/* Area Filter */}
            <select
              className="p-2 text-black bg-white border rounded-md disabled:bg-gray-700 disabled:text-gray-200"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              <option value="" className="text-black">
                Filter by Area
              </option>
              {[
                ...new Set(
                  unfilteredBrands.map((b) => b.area?._id).filter((id) => id)
                ),
              ].map((id) => {
                const name = unfilteredBrands.find((b) => b.area?._id === id)
                  ?.area?.name?.en;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>

            {/* Category Filter */}
            <select
              className="p-2 text-black bg-white border rounded-md disabled:bg-gray-700 disabled:text-gray-200"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={!selectedArea}
            >
              <option value="" className="text-black">
                {t("Filter by Category")}
              </option>
              {[
                ...new Set(
                  unfilteredBrands
                    .filter((b) => b.area?._id === selectedArea)
                    .map((b) => b.category?._id)
                    .filter((id) => id)
                ),
              ].map((id) => {
                const name = unfilteredBrands.find(
                  (b) => b.category?._id === id
                )?.category?.title?.en;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>

            {/* SubCategory Filter */}
            <select
              className="p-2 text-black bg-white border rounded-md disabled:bg-gray-700 disabled:text-gray-200"
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              disabled={!selectedCategory}
            >
              <option value="" className="text-black">
                {t("Filter by SubCategory")}
              </option>
              {categorySubCategories.map((subCat) => (
                <option
                  key={subCat._id}
                  value={subCat._id}
                  className="text-black"
                >
                  {subCat.name?.en}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Add Brand */}
        <button
          className="flex items-center gap-3 px-3 py-2 duration-300 rounded-lg bg-custom-yellow hover:bg-black group"
          onClick={() => {
            setShowForm(true);
          }}
        >
          <PlusIcon className="w-6 h-6 text-black group-hover:text-white" />
          <span className="text-black group-hover:text-white">Brand</span>
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-600">{t("Loading...")}</p>
      ) : error ? (
        <p className="text-center text-red-500">
          {t("Error")}: {error}
        </p>
      ) : allBrands?.length === 0 ? (
        <p className="text-center text-gray-500">{t("No data found")}</p>
      ) : (
        <table className="w-full my-5">
          <thead>
            <tr className="text-sm text-black bg-custom-yellow">
              {headers.map((header, index) => (
                <th key={index} className="px-2 py-2 font-bold text-center">
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {[...allBrands]
              .sort((a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0))
              .map((brand, index) => (
                <BrandsTableDetails
                  key={brand._id || index}
                  index={index + 1}
                  details={{
                    id: brand._id,
                    number: index + 1,
                    maxLength: allBrands?.length,
                    img: brand.img,
                    link: brand.link,
                    nameEN: brand.name?.en,
                    nameAR: brand.name?.ar,
                    area: brand.area?._id,
                    areaName: brand.area?.name?.en,
                    category: brand.category?._id,
                    categoryName: brand.category?.title?.en,
                    subCategory: brand.subCategory?._id,
                    subCategoryName: brand.subCategory?.name?.en,
                    desc: brand.desc,
                    sortNumber: brand.sortNumber,
                  }}
                  isEditing={editingOrder === brand._id}
                  isPricing={pricingOrder === brand._id}
                  setEditingOrder={setEditingOrder}
                  setPricingOrder={setPricingOrder}
                  onUpdate={handleUpdate}
                  onDelete={() => handleDelete(brand._id)}
                  onDown={() =>
                    handleOnDown(
                      brand._id,
                      [...allBrands].sort(
                        (a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0)
                      )[index + 1]?._id,
                      brand.sortNumber,
                      [...allBrands].sort(
                        (a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0)
                      )[index + 1]?.sortNumber
                    )
                  }
                  onTop={() =>
                    handleOnTop(
                      brand._id,
                      [...allBrands].sort(
                        (a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0)
                      )[index - 1]?._id,
                      brand.sortNumber,
                      [...allBrands].sort(
                        (a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0)
                      )[index - 1]?.sortNumber
                    )
                  }
                  selectedSubCategory={selectedSubCategory}
                  selectedCategory={selectedCategory}
                  selectedArea={selectedArea}
                />
              ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <AddBrandForm
          onClose={() => {
            setShowForm(false);
            loadBrands();
          }}
        />
      )}
    </div>
  );
};

export default BrandsSettings;
